import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Car, Plus, Edit2, Trash2, Calendar, MapPin, Fuel, Users, DollarSign, Clock, ChevronLeft, ChevronRight, Check, ChevronsUpDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { carRentals, seller } from '@/lib/api';
import { toast } from 'sonner';

const carCategories = [
  { value: 'economy', label: 'Ekonomi' },
  { value: 'compact', label: 'Kompakt' },
  { value: 'midsize', label: 'Orta Sınıf' },
  { value: 'luxury', label: 'Lüks' },
  { value: 'suv', label: 'SUV' },
  { value: 'van', label: 'Van/Minibüs' },
];

const fuelTypes = [
  { value: 'gasoline', label: 'Benzin' },
  { value: 'diesel', label: 'Dizel' },
  { value: 'electric', label: 'Elektrik' },
  { value: 'hybrid', label: 'Hibrit' },
];

const transmissionTypes = [
  { value: 'automatic', label: 'Otomatik' },
  { value: 'manual', label: 'Manuel' },
];

const carFeatures = [
  'Klima', 'GPS', 'Bluetooth', 'USB', 'Geri Görüş Kamerası',
  'Park Sensörü', 'Cruise Control', 'Deri Koltuk', 'Sunroof',
  'Bebek Koltuğu', 'Kış Lastiği', '4x4'
];

// Currencies
const currencies = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso' },
  { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
  { code: 'SAR', symbol: 'SR', name: 'Saudi Riyal' },
  { code: 'AED', symbol: 'AED', name: 'UAE Dirham' },
];

// Major airports worldwide
const airports = [
  // ========== TURKEY ==========
  { code: 'IST', name: 'Istanbul Airport', city: 'Istanbul', country: 'TR' },
  { code: 'SAW', name: 'Sabiha Gökçen', city: 'Istanbul', country: 'TR' },
  { code: 'AYT', name: 'Antalya Airport', city: 'Antalya', country: 'TR' },
  { code: 'ADB', name: 'Adnan Menderes', city: 'Izmir', country: 'TR' },
  { code: 'ESB', name: 'Esenboğa Airport', city: 'Ankara', country: 'TR' },
  { code: 'DLM', name: 'Dalaman Airport', city: 'Dalaman', country: 'TR' },
  { code: 'BJV', name: 'Bodrum Airport', city: 'Bodrum', country: 'TR' },
  { code: 'GZT', name: 'Gaziantep Airport', city: 'Gaziantep', country: 'TR' },
  { code: 'TZX', name: 'Trabzon Airport', city: 'Trabzon', country: 'TR' },
  { code: 'VAN', name: 'Van Ferit Melen', city: 'Van', country: 'TR' },
  
  // ========== MENA - Middle East ==========
  // UAE
  { code: 'DXB', name: 'Dubai International', city: 'Dubai', country: 'AE' },
  { code: 'AUH', name: 'Abu Dhabi Intl', city: 'Abu Dhabi', country: 'AE' },
  { code: 'SHJ', name: 'Sharjah Airport', city: 'Sharjah', country: 'AE' },
  // Saudi Arabia
  { code: 'JED', name: 'King Abdulaziz', city: 'Jeddah', country: 'SA' },
  { code: 'RUH', name: 'King Khalid Intl', city: 'Riyadh', country: 'SA' },
  { code: 'DMM', name: 'King Fahd Intl', city: 'Dammam', country: 'SA' },
  { code: 'MED', name: 'Prince Mohammad', city: 'Medina', country: 'SA' },
  // Qatar
  { code: 'DOH', name: 'Hamad International', city: 'Doha', country: 'QA' },
  // Kuwait
  { code: 'KWI', name: 'Kuwait International', city: 'Kuwait City', country: 'KW' },
  // Bahrain
  { code: 'BAH', name: 'Bahrain International', city: 'Manama', country: 'BH' },
  // Oman
  { code: 'MCT', name: 'Muscat International', city: 'Muscat', country: 'OM' },
  { code: 'SLL', name: 'Salalah Airport', city: 'Salalah', country: 'OM' },
  // Jordan
  { code: 'AMM', name: 'Queen Alia Intl', city: 'Amman', country: 'JO' },
  // Lebanon
  { code: 'BEY', name: 'Beirut Intl', city: 'Beirut', country: 'LB' },
  // Israel
  { code: 'TLV', name: 'Ben Gurion', city: 'Tel Aviv', country: 'IL' },
  // Iraq
  { code: 'BGW', name: 'Baghdad Intl', city: 'Baghdad', country: 'IQ' },
  { code: 'EBL', name: 'Erbil Intl', city: 'Erbil', country: 'IQ' },
  // Iran
  { code: 'IKA', name: 'Imam Khomeini', city: 'Tehran', country: 'IR' },
  { code: 'MHD', name: 'Mashhad Intl', city: 'Mashhad', country: 'IR' },
  
  // ========== MENA - North Africa ==========
  // Egypt
  { code: 'CAI', name: 'Cairo International', city: 'Cairo', country: 'EG' },
  { code: 'HRG', name: 'Hurghada Intl', city: 'Hurghada', country: 'EG' },
  { code: 'SSH', name: 'Sharm El Sheikh', city: 'Sharm El Sheikh', country: 'EG' },
  { code: 'LXR', name: 'Luxor International', city: 'Luxor', country: 'EG' },
  // Morocco
  { code: 'CMN', name: 'Mohammed V Intl', city: 'Casablanca', country: 'MA' },
  { code: 'RAK', name: 'Marrakech Menara', city: 'Marrakech', country: 'MA' },
  { code: 'AGA', name: 'Agadir Al Massira', city: 'Agadir', country: 'MA' },
  { code: 'TNG', name: 'Tangier Ibn Battouta', city: 'Tangier', country: 'MA' },
  // Tunisia
  { code: 'TUN', name: 'Tunis Carthage', city: 'Tunis', country: 'TN' },
  { code: 'DJE', name: 'Djerba Zarzis', city: 'Djerba', country: 'TN' },
  // Algeria
  { code: 'ALG', name: 'Houari Boumediene', city: 'Algiers', country: 'DZ' },
  { code: 'ORN', name: 'Oran Es Senia', city: 'Oran', country: 'DZ' },
  // Libya
  { code: 'TIP', name: 'Tripoli International', city: 'Tripoli', country: 'LY' },
  
  // ========== EUROPE - Western ==========
  // UK
  { code: 'LHR', name: 'Heathrow', city: 'London', country: 'GB' },
  { code: 'LGW', name: 'Gatwick', city: 'London', country: 'GB' },
  { code: 'STN', name: 'Stansted', city: 'London', country: 'GB' },
  { code: 'MAN', name: 'Manchester Airport', city: 'Manchester', country: 'GB' },
  { code: 'EDI', name: 'Edinburgh Airport', city: 'Edinburgh', country: 'GB' },
  // France
  { code: 'CDG', name: 'Charles de Gaulle', city: 'Paris', country: 'FR' },
  { code: 'ORY', name: 'Paris Orly', city: 'Paris', country: 'FR' },
  { code: 'NCE', name: 'Nice Côte d\'Azur', city: 'Nice', country: 'FR' },
  { code: 'LYS', name: 'Lyon Saint-Exupéry', city: 'Lyon', country: 'FR' },
  { code: 'MRS', name: 'Marseille Provence', city: 'Marseille', country: 'FR' },
  // Germany
  { code: 'FRA', name: 'Frankfurt Airport', city: 'Frankfurt', country: 'DE' },
  { code: 'MUC', name: 'Munich Airport', city: 'Munich', country: 'DE' },
  { code: 'BER', name: 'Berlin Brandenburg', city: 'Berlin', country: 'DE' },
  { code: 'DUS', name: 'Düsseldorf Airport', city: 'Düsseldorf', country: 'DE' },
  { code: 'HAM', name: 'Hamburg Airport', city: 'Hamburg', country: 'DE' },
  // Netherlands
  { code: 'AMS', name: 'Schiphol', city: 'Amsterdam', country: 'NL' },
  // Belgium
  { code: 'BRU', name: 'Brussels Airport', city: 'Brussels', country: 'BE' },
  // Switzerland
  { code: 'ZRH', name: 'Zurich Airport', city: 'Zurich', country: 'CH' },
  { code: 'GVA', name: 'Geneva Airport', city: 'Geneva', country: 'CH' },
  // Austria
  { code: 'VIE', name: 'Vienna Airport', city: 'Vienna', country: 'AT' },
  
  // ========== EUROPE - Southern ==========
  // Italy
  { code: 'FCO', name: 'Fiumicino', city: 'Rome', country: 'IT' },
  { code: 'MXP', name: 'Malpensa', city: 'Milan', country: 'IT' },
  { code: 'VCE', name: 'Marco Polo', city: 'Venice', country: 'IT' },
  { code: 'NAP', name: 'Naples Airport', city: 'Naples', country: 'IT' },
  // Spain
  { code: 'MAD', name: 'Madrid Barajas', city: 'Madrid', country: 'ES' },
  { code: 'BCN', name: 'El Prat', city: 'Barcelona', country: 'ES' },
  { code: 'PMI', name: 'Palma de Mallorca', city: 'Palma', country: 'ES' },
  { code: 'AGP', name: 'Málaga Airport', city: 'Málaga', country: 'ES' },
  { code: 'ALC', name: 'Alicante Airport', city: 'Alicante', country: 'ES' },
  // Portugal
  { code: 'LIS', name: 'Lisbon Portela', city: 'Lisbon', country: 'PT' },
  { code: 'OPO', name: 'Porto Airport', city: 'Porto', country: 'PT' },
  { code: 'FAO', name: 'Faro Airport', city: 'Faro', country: 'PT' },
  // Greece
  { code: 'ATH', name: 'Athens Intl', city: 'Athens', country: 'GR' },
  { code: 'SKG', name: 'Thessaloniki', city: 'Thessaloniki', country: 'GR' },
  { code: 'HER', name: 'Heraklion Intl', city: 'Heraklion', country: 'GR' },
  { code: 'RHO', name: 'Rhodes Intl', city: 'Rhodes', country: 'GR' },
  { code: 'JMK', name: 'Mykonos Airport', city: 'Mykonos', country: 'GR' },
  
  // ========== EUROPE - Northern ==========
  // Scandinavia
  { code: 'CPH', name: 'Copenhagen Airport', city: 'Copenhagen', country: 'DK' },
  { code: 'ARN', name: 'Stockholm Arlanda', city: 'Stockholm', country: 'SE' },
  { code: 'OSL', name: 'Oslo Gardermoen', city: 'Oslo', country: 'NO' },
  { code: 'HEL', name: 'Helsinki Vantaa', city: 'Helsinki', country: 'FI' },
  // Ireland
  { code: 'DUB', name: 'Dublin Airport', city: 'Dublin', country: 'IE' },
  
  // ========== EUROPE - Eastern ==========
  // Poland
  { code: 'WAW', name: 'Warsaw Chopin', city: 'Warsaw', country: 'PL' },
  { code: 'KRK', name: 'Kraków Airport', city: 'Kraków', country: 'PL' },
  // Czech Republic
  { code: 'PRG', name: 'Prague Airport', city: 'Prague', country: 'CZ' },
  // Hungary
  { code: 'BUD', name: 'Budapest Airport', city: 'Budapest', country: 'HU' },
  // Romania
  { code: 'OTP', name: 'Bucharest Otopeni', city: 'Bucharest', country: 'RO' },
  // Bulgaria
  { code: 'SOF', name: 'Sofia Airport', city: 'Sofia', country: 'BG' },
  // Croatia
  { code: 'ZAG', name: 'Zagreb Airport', city: 'Zagreb', country: 'HR' },
  { code: 'DBV', name: 'Dubrovnik Airport', city: 'Dubrovnik', country: 'HR' },
  { code: 'SPU', name: 'Split Airport', city: 'Split', country: 'HR' },
  // Russia
  { code: 'SVO', name: 'Sheremetyevo', city: 'Moscow', country: 'RU' },
  { code: 'DME', name: 'Domodedovo', city: 'Moscow', country: 'RU' },
  { code: 'LED', name: 'Pulkovo Airport', city: 'St. Petersburg', country: 'RU' },
  // Ukraine
  { code: 'KBP', name: 'Boryspil Intl', city: 'Kyiv', country: 'UA' },
  
  // ========== AMERICAS ==========
  { code: 'JFK', name: 'John F. Kennedy', city: 'New York', country: 'US' },
  { code: 'LAX', name: 'Los Angeles Intl', city: 'Los Angeles', country: 'US' },
  { code: 'MIA', name: 'Miami International', city: 'Miami', country: 'US' },
  { code: 'ORD', name: 'O\'Hare Intl', city: 'Chicago', country: 'US' },
  { code: 'ATL', name: 'Hartsfield-Jackson', city: 'Atlanta', country: 'US' },
  
  // ========== ASIA ==========
  { code: 'HND', name: 'Haneda', city: 'Tokyo', country: 'JP' },
  { code: 'NRT', name: 'Narita', city: 'Tokyo', country: 'JP' },
  { code: 'PEK', name: 'Beijing Capital', city: 'Beijing', country: 'CN' },
  { code: 'PVG', name: 'Shanghai Pudong', city: 'Shanghai', country: 'CN' },
  { code: 'HKG', name: 'Hong Kong Intl', city: 'Hong Kong', country: 'HK' },
  { code: 'SIN', name: 'Changi', city: 'Singapore', country: 'SG' },
  { code: 'BKK', name: 'Suvarnabhumi', city: 'Bangkok', country: 'TH' },
  { code: 'KUL', name: 'Kuala Lumpur Intl', city: 'Kuala Lumpur', country: 'MY' },
  { code: 'DEL', name: 'Indira Gandhi Intl', city: 'New Delhi', country: 'IN' },
  { code: 'BOM', name: 'Chhatrapati Shivaji', city: 'Mumbai', country: 'IN' },
];

const terminalTypes = [
  { value: 'arrival', label: 'Geliş Terminali' },
  { value: 'departure', label: 'Gidiş Terminali' },
  { value: 'both', label: 'Her İki Terminal' },
];

const CarRentalTab = () => {
  const { t } = useTranslation();
  const [cars, setCars] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCar, setShowAddCar] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [chassisError, setChassisError] = useState('');
  const [chassisChecking, setChassisChecking] = useState(false);
  const [pickupAirportSearch, setPickupAirportSearch] = useState('');
  const [dropoffAirportSearch, setDropoffAirportSearch] = useState('');
  const [pickupAirportOpen, setPickupAirportOpen] = useState(false);
  const [dropoffAirportOpen, setDropoffAirportOpen] = useState(false);
  const [newCar, setNewCar] = useState({
    title: '',
    description: '',
    chassis_number: '',
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    fuel_type: 'gasoline',
    transmission: 'automatic',
    seats: 5,
    category: 'economy',
    images: [''],
    features: [],
    pricing: { hourly: '', daily: '', weekly: '', monthly: '' },
    currency: 'USD',
    location: '',
    pickup_location: {
      address: '',
      city: '',
      country: '',
      airport_code: '',
      terminal: '',
      is_airport: false
    },
    dropoff_location: {
      address: '',
      city: '',
      country: '',
      airport_code: '',
      terminal: '',
      is_airport: false
    },
    min_rental_hours: 1,
    max_rental_days: 30,
    deposit_amount: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [carsRes, bookingsRes] = await Promise.all([
        seller.carRentals(),
        seller.carBookings()
      ]);
      setCars(carsRes.data || []);
      setBookings(bookingsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch car rentals:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkChassisNumber = async (chassisNum) => {
    if (!chassisNum || chassisNum.length < 5) {
      setChassisError('');
      return;
    }
    setChassisChecking(true);
    try {
      const response = await carRentals.checkChassis(chassisNum);
      if (response.data.exists) {
        setChassisError(t('seller.chassisAlreadyExists'));
      } else {
        setChassisError('');
      }
    } catch (error) {
      console.error('Chassis check error:', error);
    } finally {
      setChassisChecking(false);
    }
  };

  // Filter airports based on search term
  const filterAirports = (searchTerm) => {
    if (!searchTerm) return airports;
    const term = searchTerm.toLowerCase();
    return airports.filter(a => 
      a.code.toLowerCase().includes(term) ||
      a.name.toLowerCase().includes(term) ||
      a.city.toLowerCase().includes(term)
    );
  };

  const handleAddCar = async (e) => {
    e.preventDefault();
    
    if (chassisError) {
      toast.error(t('seller.chassisAlreadyExists'));
      return;
    }
    
    if (!newCar.chassis_number || newCar.chassis_number.length < 5) {
      toast.error(t('seller.chassisRequired'));
      return;
    }
    
    
    try {
      const carData = {
        ...newCar,
        chassis_number: newCar.chassis_number.toUpperCase(),
        pricing: {
          hourly: newCar.pricing.hourly ? parseFloat(newCar.pricing.hourly) : null,
          daily: newCar.pricing.daily ? parseFloat(newCar.pricing.daily) : null,
          weekly: newCar.pricing.weekly ? parseFloat(newCar.pricing.weekly) : null,
          monthly: newCar.pricing.monthly ? parseFloat(newCar.pricing.monthly) : null,
          currency: newCar.currency
        },
        currency: newCar.currency,
        deposit_amount: newCar.deposit_amount ? parseFloat(newCar.deposit_amount) : null,
        images: newCar.images.filter(img => img.trim()),
        pickup_location: newCar.pickup_location.is_airport || newCar.pickup_location.address ? newCar.pickup_location : null,
        dropoff_location: newCar.dropoff_location.is_airport || newCar.dropoff_location.address ? newCar.dropoff_location : null
      };
      await carRentals.create(carData);
      toast.success(t('seller.carCreated'));
      setShowAddCar(false);
      resetForm();
      fetchData();
    } catch (error) {
      if (error.response?.data?.detail?.includes('şasi')) {
        toast.error(t('seller.chassisAlreadyExists'));
      } else {
        toast.error(t('seller.carCreateFailed'));
      }
    }
  };

  const handleDeleteCar = async (carId) => {
    if (!window.confirm(t('seller.confirmDeleteCar'))) return;
    try {
      await carRentals.delete(carId);
      toast.success(t('seller.carDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('seller.carDeleteFailed'));
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      await seller.updateCarBookingStatus(bookingId, status);
      toast.success(t('seller.bookingStatusUpdated'));
      fetchData();
    } catch (error) {
      toast.error(t('seller.bookingStatusUpdateFailed'));
    }
  };

  const resetForm = () => {
    setNewCar({
      title: '',
      description: '',
      chassis_number: '',
      brand: '',
      model: '',
      year: new Date().getFullYear(),
      fuel_type: 'gasoline',
      transmission: 'automatic',
      seats: 5,
      category: 'economy',
      images: [''],
      features: [],
      pricing: { hourly: '', daily: '', weekly: '', monthly: '' },
      currency: 'USD',
      location: '',
      pickup_location: {
        address: '',
        city: '',
        country: '',
        airport_code: '',
        terminal: '',
        is_airport: false
      },
      dropoff_location: {
        address: '',
        city: '',
        country: '',
        airport_code: '',
        terminal: '',
        is_airport: false
      },
      min_rental_hours: 1,
      max_rental_days: 30,
      deposit_amount: ''
    });
  };

  const toggleFeature = (feature) => {
    if (newCar.features.includes(feature)) {
      setNewCar({ ...newCar, features: newCar.features.filter(f => f !== feature) });
    } else {
      setNewCar({ ...newCar, features: [...newCar.features, feature] });
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    return { daysInMonth, firstDayOfMonth };
  };

  const renderCalendar = () => {
    const { daysInMonth, firstDayOfMonth } = getDaysInMonth(currentMonth);
    const days = [];
    
    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const hasBooking = bookings.some(b => 
        b.car_rental_id === selectedCar?.id && 
        new Date(b.start_datetime) <= new Date(dateStr) &&
        new Date(b.end_datetime) >= new Date(dateStr)
      );
      
      days.push(
        <div
          key={day}
          className={`p-2 text-center rounded-lg cursor-pointer transition-colors ${
            hasBooking
              ? 'bg-red-100 text-red-800'
              : 'hover:bg-primary/10'
          }`}
        >
          {day}
        </div>
      );
    }
    
    return days;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Car className="w-6 h-6" />
            {t('seller.carRentals')}
          </h2>
          <p className="text-muted-foreground">{t('seller.manageCarRentals')}</p>
        </div>
        <Dialog open={showAddCar} onOpenChange={setShowAddCar}>
          <DialogTrigger asChild>
            <Button className="rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              {t('seller.addCar')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('seller.addNewCar')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddCar} className="space-y-4">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>{t('seller.carTitle')}</Label>
                  <Input
                    value={newCar.title}
                    onChange={(e) => setNewCar({ ...newCar, title: e.target.value })}
                    placeholder="Örn: Toyota Corolla 2023"
                    required
                  />
                </div>
                <div>
                  <Label>{t('seller.carCategory')}</Label>
                  <Select value={newCar.category} onValueChange={(v) => setNewCar({ ...newCar, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {carCategories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Chassis Number */}
              <div className="border rounded-lg p-4 bg-amber-50/50">
                <Label className="text-base font-medium">🔢 {t('seller.chassisNumber')}</Label>
                <p className="text-xs text-muted-foreground mb-2">{t('seller.chassisNumberDesc')}</p>
                <div className="relative">
                  <Input
                    value={newCar.chassis_number}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      setNewCar({ ...newCar, chassis_number: value });
                      if (value.length >= 5) {
                        checkChassisNumber(value);
                      } else {
                        setChassisError('');
                      }
                    }}
                    placeholder="WVWZZZ3CZWE123456"
                    className={chassisError ? 'border-red-500' : ''}
                    required
                    maxLength={17}
                  />
                  {chassisChecking && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
                    </div>
                  )}
                </div>
                {chassisError && (
                  <p className="text-sm text-red-500 mt-1">⚠️ {chassisError}</p>
                )}
                {!chassisError && newCar.chassis_number.length >= 5 && !chassisChecking && (
                  <p className="text-sm text-green-600 mt-1">✅ {t('seller.chassisAvailable')}</p>
                )}
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>{t('seller.brand')}</Label>
                  <Input
                    value={newCar.brand}
                    onChange={(e) => setNewCar({ ...newCar, brand: e.target.value })}
                    placeholder="Toyota"
                    required
                  />
                </div>
                <div>
                  <Label>{t('seller.model')}</Label>
                  <Input
                    value={newCar.model}
                    onChange={(e) => setNewCar({ ...newCar, model: e.target.value })}
                    placeholder="Corolla"
                    required
                  />
                </div>
                <div>
                  <Label>{t('seller.year')}</Label>
                  <Input
                    type="number"
                    min="2000"
                    max={new Date().getFullYear() + 1}
                    value={newCar.year}
                    onChange={(e) => setNewCar({ ...newCar, year: parseInt(e.target.value) })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label>{t('seller.description')}</Label>
                <Textarea
                  value={newCar.description}
                  onChange={(e) => setNewCar({ ...newCar, description: e.target.value })}
                  placeholder={t('seller.carDescriptionPlaceholder')}
                  required
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>{t('seller.fuelType')}</Label>
                  <Select value={newCar.fuel_type} onValueChange={(v) => setNewCar({ ...newCar, fuel_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {fuelTypes.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('seller.transmission')}</Label>
                  <Select value={newCar.transmission} onValueChange={(v) => setNewCar({ ...newCar, transmission: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {transmissionTypes.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('seller.seats')}</Label>
                  <Input
                    type="number"
                    min="2"
                    max="15"
                    value={newCar.seats}
                    onChange={(e) => setNewCar({ ...newCar, seats: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <Label>{t('seller.location')}</Label>
                <Input
                  value={newCar.location}
                  onChange={(e) => setNewCar({ ...newCar, location: e.target.value })}
                  placeholder="İstanbul, Türkiye"
                  required
                />
              </div>

              {/* Pricing */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">💰 {t('seller.pricing')}</Label>
                  <Select value={newCar.currency} onValueChange={(v) => setNewCar({ ...newCar, currency: v })}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map(c => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.symbol} {c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs">{t('seller.hourlyPrice')}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newCar.pricing.hourly}
                      onChange={(e) => setNewCar({ 
                        ...newCar, 
                        pricing: { ...newCar.pricing, hourly: e.target.value } 
                      })}
                      placeholder="25.00"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('seller.dailyPrice')}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newCar.pricing.daily}
                      onChange={(e) => setNewCar({ 
                        ...newCar, 
                        pricing: { ...newCar.pricing, daily: e.target.value } 
                      })}
                      placeholder="150.00"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('seller.weeklyPrice')}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newCar.pricing.weekly}
                      onChange={(e) => setNewCar({ 
                        ...newCar, 
                        pricing: { ...newCar.pricing, weekly: e.target.value } 
                      })}
                      placeholder="800.00"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('seller.monthlyPrice')}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newCar.pricing.monthly}
                      onChange={(e) => setNewCar({ 
                        ...newCar, 
                        pricing: { ...newCar.pricing, monthly: e.target.value } 
                      })}
                      placeholder="2500.00"
                    />
                  </div>
                </div>
              </div>

              {/* Pickup Location */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">📍 {t('seller.pickupLocation')}</Label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newCar.pickup_location.is_airport}
                      onChange={(e) => setNewCar({
                        ...newCar,
                        pickup_location: { ...newCar.pickup_location, is_airport: e.target.checked }
                      })}
                      className="rounded"
                    />
                    {t('seller.isAirport')}
                  </label>
                </div>
                {newCar.pickup_location.is_airport ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">{t('seller.searchAirport')}</Label>
                      <Popover open={pickupAirportOpen} onOpenChange={setPickupAirportOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={pickupAirportOpen}
                            className="w-full justify-between font-normal"
                          >
                            {newCar.pickup_location.airport_code ? (
                              <span>
                                <span className="font-mono font-bold">{newCar.pickup_location.airport_code}</span>
                                {' - '}
                                {airports.find(a => a.code === newCar.pickup_location.airport_code)?.name}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">{t('seller.selectAirport')}</span>
                            )}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0" align="start">
                          <Command>
                            <CommandInput 
                              placeholder={t('seller.searchAirportPlaceholder')} 
                              value={pickupAirportSearch}
                              onValueChange={setPickupAirportSearch}
                            />
                            <CommandList>
                              <CommandEmpty>{t('seller.noAirportsFound')}</CommandEmpty>
                              <CommandGroup>
                                {filterAirports(pickupAirportSearch).slice(0, 50).map((airport) => (
                                  <CommandItem
                                    key={airport.code}
                                    value={`${airport.code} ${airport.name} ${airport.city}`}
                                    onSelect={() => {
                                      setNewCar({
                                        ...newCar,
                                        pickup_location: {
                                          ...newCar.pickup_location,
                                          airport_code: airport.code,
                                          city: airport.city,
                                          country: airport.country,
                                          address: airport.name
                                        }
                                      });
                                      setPickupAirportSearch('');
                                      setPickupAirportOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        newCar.pickup_location.airport_code === airport.code ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    <span className="font-mono font-bold mr-2">{airport.code}</span>
                                    <span className="truncate">{airport.name}</span>
                                    <span className="ml-auto text-muted-foreground text-xs">{airport.city}</span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label className="text-xs">{t('seller.terminal')}</Label>
                      <Select 
                        value={newCar.pickup_location.terminal}
                        onValueChange={(v) => setNewCar({
                          ...newCar,
                          pickup_location: { ...newCar.pickup_location, terminal: v }
                        })}
                      >
                        <SelectTrigger><SelectValue placeholder={t('seller.selectTerminal')} /></SelectTrigger>
                        <SelectContent>
                          {terminalTypes.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">{t('seller.address')}</Label>
                      <Input
                        value={newCar.pickup_location.address}
                        onChange={(e) => setNewCar({
                          ...newCar,
                          pickup_location: { ...newCar.pickup_location, address: e.target.value }
                        })}
                        placeholder={t('seller.addressPlaceholder')}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t('seller.city')}</Label>
                      <Input
                        value={newCar.pickup_location.city}
                        onChange={(e) => setNewCar({
                          ...newCar,
                          pickup_location: { ...newCar.pickup_location, city: e.target.value }
                        })}
                        placeholder="İstanbul"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Dropoff Location */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">📍 {t('seller.dropoffLocation')}</Label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newCar.dropoff_location.is_airport}
                      onChange={(e) => setNewCar({
                        ...newCar,
                        dropoff_location: { ...newCar.dropoff_location, is_airport: e.target.checked }
                      })}
                      className="rounded"
                    />
                    {t('seller.isAirport')}
                  </label>
                </div>
                {newCar.dropoff_location.is_airport ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">{t('seller.searchAirport')}</Label>
                      <Popover open={dropoffAirportOpen} onOpenChange={setDropoffAirportOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={dropoffAirportOpen}
                            className="w-full justify-between font-normal"
                          >
                            {newCar.dropoff_location.airport_code ? (
                              <span>
                                <span className="font-mono font-bold">{newCar.dropoff_location.airport_code}</span>
                                {' - '}
                                {airports.find(a => a.code === newCar.dropoff_location.airport_code)?.name}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">{t('seller.selectAirport')}</span>
                            )}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0" align="start">
                          <Command>
                            <CommandInput 
                              placeholder={t('seller.searchAirportPlaceholder')} 
                              value={dropoffAirportSearch}
                              onValueChange={setDropoffAirportSearch}
                            />
                            <CommandList>
                              <CommandEmpty>{t('seller.noAirportsFound')}</CommandEmpty>
                              <CommandGroup>
                                {filterAirports(dropoffAirportSearch).slice(0, 50).map((airport) => (
                                  <CommandItem
                                    key={airport.code}
                                    value={`${airport.code} ${airport.name} ${airport.city}`}
                                    onSelect={() => {
                                      setNewCar({
                                        ...newCar,
                                        dropoff_location: {
                                          ...newCar.dropoff_location,
                                          airport_code: airport.code,
                                          city: airport.city,
                                          country: airport.country,
                                          address: airport.name
                                        }
                                      });
                                      setDropoffAirportSearch('');
                                      setDropoffAirportOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        newCar.dropoff_location.airport_code === airport.code ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    <span className="font-mono font-bold mr-2">{airport.code}</span>
                                    <span className="truncate">{airport.name}</span>
                                    <span className="ml-auto text-muted-foreground text-xs">{airport.city}</span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label className="text-xs">{t('seller.terminal')}</Label>
                      <Select 
                        value={newCar.dropoff_location.terminal}
                        onValueChange={(v) => setNewCar({
                          ...newCar,
                          dropoff_location: { ...newCar.dropoff_location, terminal: v }
                        })}
                      >
                        <SelectTrigger><SelectValue placeholder={t('seller.selectTerminal')} /></SelectTrigger>
                        <SelectContent>
                          {terminalTypes.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">{t('seller.address')}</Label>
                      <Input
                        value={newCar.dropoff_location.address}
                        onChange={(e) => setNewCar({
                          ...newCar,
                          dropoff_location: { ...newCar.dropoff_location, address: e.target.value }
                        })}
                        placeholder={t('seller.addressPlaceholder')}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t('seller.city')}</Label>
                      <Input
                        value={newCar.dropoff_location.city}
                        onChange={(e) => setNewCar({
                          ...newCar,
                          dropoff_location: { ...newCar.dropoff_location, city: e.target.value }
                        })}
                        placeholder="İstanbul"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Deposit */}
              <div>
                <Label>{t('seller.depositAmount')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newCar.deposit_amount}
                  onChange={(e) => setNewCar({ ...newCar, deposit_amount: e.target.value })}
                  placeholder="500.00"
                />
              </div>

              {/* Features */}
              <div className="border rounded-lg p-4 space-y-3">
                <Label className="text-base font-medium">🚗 {t('seller.features')}</Label>
                <div className="flex flex-wrap gap-2">
                  {carFeatures.map(feature => (
                    <button
                      key={feature}
                      type="button"
                      onClick={() => toggleFeature(feature)}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${
                        newCar.features.includes(feature)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-secondary hover:bg-secondary/80'
                      }`}
                    >
                      {feature}
                    </button>
                  ))}
                </div>
              </div>

              {/* Image URL */}
              <div>
                <Label>{t('seller.imageUrl')}</Label>
                <Input
                  value={newCar.images[0]}
                  onChange={(e) => setNewCar({ ...newCar, images: [e.target.value] })}
                  placeholder="https://example.com/car-image.jpg"
                />
              </div>

              <Button type="submit" className="w-full rounded-full">
                {t('seller.createCar')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('seller.totalCars')}</p>
                <p className="text-2xl font-bold">{cars.length}</p>
              </div>
              <Car className="w-8 h-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('seller.activeBookings')}</p>
                <p className="text-2xl font-bold">
                  {bookings.filter(b => ['pending', 'confirmed', 'active'].includes(b.status)).length}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('seller.totalRevenue')}</p>
                <p className="text-2xl font-bold">
                  ${bookings.filter(b => b.status === 'completed').reduce((sum, b) => sum + b.total_price, 0).toFixed(2)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cars Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('seller.myCars')}</CardTitle>
        </CardHeader>
        <CardContent>
          {cars.length === 0 ? (
            <div className="text-center py-8">
              <Car className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">{t('seller.noCars')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('seller.car')}</TableHead>
                  <TableHead>{t('seller.category')}</TableHead>
                  <TableHead>{t('seller.pricing')}</TableHead>
                  <TableHead>{t('seller.location')}</TableHead>
                  <TableHead className="text-right">{t('seller.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cars.map((car) => (
                  <TableRow key={car.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-12 rounded-lg overflow-hidden bg-secondary">
                          <img
                            src={car.images?.[0] || '/placeholder-car.jpg'}
                            alt={car.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <p className="font-medium">{car.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {car.brand} {car.model} • {car.year}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {carCategories.find(c => c.value === car.category)?.label || car.category}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm space-y-1">
                        {car.pricing?.hourly && <div>⏰ {currencies.find(c => c.code === car.currency)?.symbol || '$'}{car.pricing.hourly}/{t('seller.hour')}</div>}
                        {car.pricing?.daily && <div>📅 {currencies.find(c => c.code === car.currency)?.symbol || '$'}{car.pricing.daily}/{t('seller.day')}</div>}
                        {car.pricing?.weekly && <div>📆 {currencies.find(c => c.code === car.currency)?.symbol || '$'}{car.pricing.weekly}/{t('seller.week')}</div>}
                        {car.pricing?.monthly && <div>🗓️ {currencies.find(c => c.code === car.currency)?.symbol || '$'}{car.pricing.monthly}/{t('seller.month')}</div>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <MapPin className="w-3 h-3" />
                        {car.location}
                      </div>
                      {car.pickup_location?.is_airport && (
                        <div className="text-xs text-muted-foreground mt-1">
                          ✈️ {car.pickup_location.airport_code}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedCar(car);
                            setShowCalendar(true);
                          }}
                          title={t('seller.viewCalendar')}
                        >
                          <Calendar className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => handleDeleteCar(car.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('seller.carBookings')}</CardTitle>
        </CardHeader>
        <CardContent>
          {bookings.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">{t('seller.noBookings')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('seller.booking')}</TableHead>
                  <TableHead>{t('seller.customer')}</TableHead>
                  <TableHead>{t('seller.dates')}</TableHead>
                  <TableHead>{t('seller.total')}</TableHead>
                  <TableHead>{t('seller.status')}</TableHead>
                  <TableHead className="text-right">{t('seller.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.map((booking) => (
                  <TableRow key={booking.id}>
                    <TableCell>
                      <p className="font-medium">#{booking.id.slice(0, 8)}</p>
                      <p className="text-xs text-muted-foreground">{booking.rental_type}</p>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium">{booking.customer_name}</p>
                      <p className="text-xs text-muted-foreground">{booking.customer_email}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">
                        {new Date(booking.start_datetime).toLocaleDateString()} -
                        {new Date(booking.end_datetime).toLocaleDateString()}
                      </p>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium">${booking.total_price?.toFixed(2)}</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant={
                        booking.status === 'confirmed' ? 'default' :
                        booking.status === 'active' ? 'default' :
                        booking.status === 'completed' ? 'secondary' :
                        booking.status === 'cancelled' ? 'destructive' : 'outline'
                      }>
                        {booking.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Select
                        value={booking.status}
                        onValueChange={(status) => handleUpdateBookingStatus(booking.id, status)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Beklemede</SelectItem>
                          <SelectItem value="confirmed">Onaylandı</SelectItem>
                          <SelectItem value="active">Aktif</SelectItem>
                          <SelectItem value="completed">Tamamlandı</SelectItem>
                          <SelectItem value="cancelled">İptal</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Calendar Dialog */}
      <Dialog open={showCalendar} onOpenChange={setShowCalendar}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              📅 {selectedCar?.title} - {t('seller.availabilityCalendar')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="font-medium">
                {currentMonth.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {['Pz', 'Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct'].map(day => (
                <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                  {day}
                </div>
              ))}
              {renderCalendar()}
            </div>
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-100 rounded"></div>
                <span>{t('seller.booked')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-white border rounded"></div>
                <span>{t('seller.available')}</span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CarRentalTab;





